import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  List,
  Stack,
  alpha,
  Dialog,
  Avatar,
  Button,
  Divider,
  ListItem,
  Typography,
  IconButton,
  DialogTitle,
  ListItemText,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';

import {
  fetchDepartments,
  getDepartmentImageUrl,
  fetchAllowedSubmitters,
  updateAllowedSubmitters,
} from 'src/services/department.service';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

ManageSubmittersModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  department: PropTypes.object.isRequired,
};

export default function ManageSubmittersModal({ open, onClose, department }) {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();

  const [allDepartments, setAllDepartments] = useState([]);
  const [selectedSenderIds, setSelectedSenderIds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Background-fetches the lightweight whitelists directly from our new API
  const loadDepartmentsData = useCallback(async () => {
    if (!open || !department?.id) return;
    setLoading(true);
    try {
      const [res, allowedList] = await Promise.all([
        fetchDepartments(),
        fetchAllowedSubmitters(department.id),
      ]);

      setAllDepartments(res || []);

      const currentSenderIds = (allowedList || []).map((d) => d.id);
      setSelectedSenderIds(currentSenderIds);
    } catch (err) {
      console.error('Failed to load departments metadata', err);
    } finally {
      setLoading(false);
    }
  }, [open, department?.id]);

  useEffect(() => {
    loadDepartmentsData();
  }, [loadDepartmentsData]);

  // 2. Separate into Allowed and Blocked lists in memory
  const { allowedDepartments, blockedDepartments } = useMemo(() => {
    const allowed = [];
    const blocked = [];

    allDepartments.forEach((dept) => {
      if (dept.id === department.id) return;

      if (selectedSenderIds.includes(dept.id)) {
        allowed.push(dept);
      } else {
        blocked.push(dept);
      }
    });

    return { allowedDepartments: allowed, blockedDepartments: blocked };
  }, [allDepartments, selectedSenderIds, department.id]);

  const handleToggleAccess = (deptId, isPermitting) => {
    setSelectedSenderIds((prev) =>
      isPermitting ? [...prev, deptId] : prev.filter((id) => id !== deptId)
    );
  };

  const handleClose = () => {
    setSelectedSenderIds([]);
    onClose();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateAllowedSubmitters(department.id, selectedSenderIds);

      showToast({
        message: t('successMessages.successDepartmentUpdate'),
        severity: 'success',
        props: { hideToast },
      });

      const updatedAllowedSenderDepartments = allDepartments.filter((d) =>
        selectedSenderIds.includes(d.id)
      );
      setSelectedSenderIds([]);
      onClose({
        ...department,
        allowedSenderDepartments: updatedAllowedSenderDepartments,
      });
    } catch (err) {
      console.error('Failed to save whitelisted senders', err);
      showToast({
        message: t('errorMessages.failedDepartmentUpdate'),
        severity: 'error',
        props: { hideToast },
      });
    } finally {
      setLoading(false);
    }
  };

  const renderDeptRow = (dept, statusType) => {
    const isSelf = dept.id === department.id;
    const version = dept.updatedAt ? new Date(dept.updatedAt).getTime() : 'default';
    const isCustomImage = dept.image === 'HAS_IMAGE';

    let rowBgColor = 'transparent';
    let labelColor = 'text.primary';
    if (isSelf) {
      rowBgColor = alpha('#2065D1', 0.08);
      labelColor = '#2065D1';
    } else if (statusType === 'ALLOWED') {
      rowBgColor = alpha('#00A76F', 0.04);
    } else if (statusType === 'BLOCKED') {
      rowBgColor = alpha('#FF4842', 0.04);
    }

    return (
      <ListItem
        key={dept.id}
        sx={{
          mb: 1,
          borderRadius: '12px',
          bgcolor: rowBgColor,
          border: isSelf ? '1.5px solid #2065D1' : '1px solid',
          borderColor: isSelf ? '#2065D1' : 'divider',
          transition: 'all 0.2s',
        }}
        secondaryAction={
          isSelf ? (
            <Button
              disabled
              size="small"
              variant="outlined"
              sx={{
                color: '#2065D1 !important',
                borderColor: '#2065D1 !important',
                fontWeight: 800,
                borderRadius: '8px',
              }}
            >
              {t('tickettt.selfWhitelist')}
            </Button>
          ) : statusType === 'ALLOWED' ? (
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => handleToggleAccess(dept.id, false)}
              sx={{ borderRadius: '8px', fontWeight: 800, textTransform: 'none' }}
            >
              {t('buttons.unpermit')}
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleToggleAccess(dept.id, true)}
              sx={{
                bgcolor: '#00A76F',
                '&:hover': { bgcolor: '#008F5D' },
                borderRadius: '8px',
                fontWeight: 800,
                textTransform: 'none',
              }}
            >
              {t('buttons.permit')}
            </Button>
          )
        }
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '80%' }}>
          <Avatar
            src={
              isCustomImage
                ? `${getDepartmentImageUrl(
                    dept.id,
                    dept.updatedAt || dept.createdAt
                  )}?v=${version}` // Explicitly appends the version to force instant browser cache-busting inside the modal
                : '/assets/images/departments/defaultDepartment.png'
            }
            imgProps={{ loading: 'lazy' }}
            sx={{ width: 40, height: 40, border: '1px solid', borderColor: 'divider' }}
          />
          <ListItemText
            primary={dept.name}
            secondary={
              isSelf
                ? t('tickettt.selfWhitelistDesc')
                : dept.description || t('tickettt.noDescription')
            }
            primaryTypographyProps={{
              variant: 'subtitle2',
              fontWeight: 800,
              color: labelColor,
            }}
            secondaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
          />
        </Stack>
      </ListItem>
    );
  };

  const selfEntity = allDepartments.find((d) => d.id === department.id);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(22, 27, 34, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }} component="div">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {t('tickettt.manageSubmissionScope')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ fontWeight: 600 }}
            >
              {t('tickettt.scopeAdjustmentInstruction')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ mr: -1.5 }}>
            <Iconify icon="eva:close-square-fill" width={30} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Scrollbar sx={{ maxHeight: 450, pr: 1 }}>
          {loading && allDepartments.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress color="success" />
            </Box>
          ) : (
            <List disablePadding>
              {/* SECTION 1: SELF-WHITELIST HIGHLIGHT */}
              {selfEntity && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.disabled',
                      fontWeight: 900,
                      mb: 1,
                      display: 'block',
                    }}
                  >
                    {t('tickettt.currentDepartment')}
                  </Typography>
                  {renderDeptRow(selfEntity, 'SELF')}
                  <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />
                </>
              )}

              {/* SECTION 2: APPROVED DEPARTMENTS (FAINT GREEN) */}
              <Typography
                variant="overline"
                sx={{ color: '#00A76F', fontWeight: 900, mb: 1, display: 'block' }}
              >
                {t('tickettt.allowedSenderDepartments')} ({allowedDepartments.length})
              </Typography>
              {allowedDepartments.length > 0 ? (
                allowedDepartments.map((dept) => renderDeptRow(dept, 'ALLOWED'))
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    pl: 1,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  {t('tickettt.noAllowedSendersYet')}
                </Typography>
              )}

              <Box sx={{ height: 16 }} />

              {/* SECTION 3: BLOCKED DEPARTMENTS (FAINT RED) */}
              <Typography
                variant="overline"
                sx={{ color: '#FF4842', fontWeight: 900, mb: 1, display: 'block' }}
              >
                {t('tickettt.blockedSenderDepartments')} ({blockedDepartments.length})
              </Typography>
              {blockedDepartments.length > 0 ? (
                blockedDepartments.map((dept) => renderDeptRow(dept, 'BLOCKED'))
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    pl: 1,
                    display: 'block',
                  }}
                >
                  {t('tickettt.allDepartmentsAllowed')}
                </Typography>
              )}
            </List>
          )}
        </Scrollbar>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5 }}>
        <Button color="inherit" onClick={handleClose} sx={{ fontWeight: 700 }}>
          {t('buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={loading}
          onClick={handleSave}
          sx={{
            bgcolor: '#00A76F',
            fontWeight: 800,
            borderRadius: '12px',
            px: 4,
            '&:hover': { bgcolor: '#008F5D' },
          }}
        >
          {t('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
